import { EstimateV2API } from '@business/api/estimate-v2';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { EMPTY, Observable, Subject, Subscription, defer } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import {
  EstimateGroupLineItemData,
  EstimateItemFormData,
  EstimateItemGroupData,
} from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';
import {
  CompactTMEstimateResponse,
  EstimateSearchParams,
  TMEstimateFeeId,
  TMEstimateGroupId,
  TMEstimateId,
  TMEstimateLineItemId,
  TMEstimateResponse,
} from '@business/entities/tm';
import { AddEstimateFormData } from '@modules/organizations/OrganizationPage/Components/Design/AddEstimateModal/AddEstimateModal';
import { OrgId } from '@business/entities/organization';
import { UserId } from '@business/entities/account';
import { R } from '@core/utils/r';
import { CategoryOptionId } from '@business/entities/category_option';
import { ServiceProviderId } from '@business/entities/tm/TmServiceProviderResponse';
import { EstimateFilter } from '@modules/organizations/OrganizationPage/Components/DesignV2/DesignTabV2Bloc';

export class EstimateV2Bloc extends BaseBloc {
  private readonly estimateRepo = new Repository<TMEstimateResponse | CompactTMEstimateResponse>({
    getItemId: item => item.id,
  });

  readonly estimates$ = this.estimateRepo.items$.pipe(map(items => R.sortBy(items, s => R.toNumber(s.id) * -1)));

  readonly selectEstimate = this.estimateRepo.selectItem;

  onReset() {
    this.estimateRepo.reset();
  }

  // Line-item edits (qty / price / we-provide) run strictly one-at-a-time in submission order.
  // Every response carries the FULL recalculated estimate: if two edits overlapped, the slower
  // (older) response could land last and overwrite the newer recalculation in the repo.
  private readonly updateJobs$ = new Subject<{ exec: () => Observable<unknown>; out$: Subject<unknown> }>();
  private updateJobsSub: Subscription | null = null;

  private ensureUpdateQueue() {
    if (this.updateJobsSub) return;
    this.updateJobsSub = this.updateJobs$
      .pipe(
        concatMap(({ exec, out$ }) =>
          exec().pipe(
            tap({
              next: value => out$.next(value),
              error: error => out$.error(error),
              complete: () => out$.complete(),
            }),
            // A failed job must not kill the queue — its error already went to the caller via out$.
            catchError(() => EMPTY),
          ),
        ),
      )
      .subscribe();
  }

  private enqueue<T>(exec: () => Observable<T>): Observable<T> {
    return defer(() => {
      this.ensureUpdateQueue();
      const out$ = new Subject<T>();
      this.updateJobs$.next({ exec: exec as () => Observable<unknown>, out$: out$ as Subject<unknown> });
      return out$;
    });
  }

  fetchEstimates = (orgId?: OrgId, isCompact?: boolean, filters?: EstimateFilter) => {
    return EstimateV2API.fetchEstimates(orgId, isCompact, filters).pipe(
      this.estimateRepo.ops.addMany(items => items),
      handleMessage({ error: requestMessage('fetch_estimate_error') }),
    );
  };

  fetchEstimatesPaginated = (query?: EstimateSearchParams) => {
    return EstimateV2API.fetchEstimatesPaginated(query);
  };

  fetchEstimate = (id: TMEstimateId) => {
    return EstimateV2API.fetchEstimate(id).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_estimate_error') }),
    );
  };

  createEstimate = (data: AddEstimateFormData) => {
    return EstimateV2API.createEstimate(data).pipe(
      this.estimateRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_estimate'),
      }),
    );
  };

  recalculateEstimate = (id: TMEstimateId) => {
    return EstimateV2API.recalculateEstimate(id).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('recalculate_estimate'),
      }),
    );
  };

  updateEstimate = (id: TMEstimateId, data: EstimateItemFormData) => {
    return EstimateV2API.updateEstimate(id, data).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_estimate'),
      }),
    );
  };

  deleteEstimate = (id: TMEstimateId) => {
    return showDeleteConfirmation('Delete Estimate', `Do you really want to remove this estimate?`).pipe(
      switchMap(() => EstimateV2API.deleteEstimate(id)),
      this.estimateRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_estimate'),
      }),
    );
  };

  restoreEstimate = (id: TMEstimateId) => {
    return showConfirmation('Restore Estimate', `Do you really want to restore this estimate?`).pipe(
      switchMap(() => EstimateV2API.restoreEstimate(id)),
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('restore_estimate'),
      }),
    );
  };

  addOrUpdateGroup = (estimateId: TMEstimateId, data: EstimateItemGroupData) => {
    return EstimateV2API.addOrUpdateGroup(estimateId, data).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('save_estimate_group'),
      }),
    );
  };

  updateOption = (groupId: TMEstimateGroupId, optionId: CategoryOptionId, providerId: ServiceProviderId) => {
    return EstimateV2API.updateOption(groupId, optionId, providerId).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      // handleMessage({
      //   type: requestMessage('update_estimate_option'),
      // }),
    );
  };

  updateNote = (itemId: TMEstimateLineItemId, note: string) => {
    return EstimateV2API.updateNote(itemId, note);
  };

  updateFee = (id: TMEstimateFeeId, value: number) => {
    return EstimateV2API.updateFee(id, value).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_estimate_fee'),
      }),
    );
  };

  // The estimate page's Estimator avatar (null removes it)
  updateEstimator = (id: TMEstimateId, estimatorUserId: UserId | null) => {
    return EstimateV2API.updateEstimator(id, estimatorUserId).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_estimate_estimator'),
      }),
    );
  };

  // An option's description (Options tab "Edit Description"); proposals not signed yet get it too (backend)
  updateDescription = (id: TMEstimateId, description: string) => {
    return EstimateV2API.updateDescription(id, description).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_estimate_description'),
      }),
    );
  };

  updateWeProvide = (estimateId: TMEstimateId, groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) => {
    return this.enqueue(() =>
      // The whole estimate comes back (the item and the provider Minimum Invoice lines that follow it); callers get the item
      EstimateV2API.updateWeProvide(groupId, item).pipe(
        this.estimateRepo.ops.upsertOne(estimate => ({ item: estimate })),
        map(estimate => estimate.groups.find(g => g.id === groupId)?.items.find(i => i.id === item.id)),
      ),
    );
  };

  qtyUpdate = (estimateId: TMEstimateId, groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) => {
    return this.enqueue(() =>
      EstimateV2API.qtyUpdate(groupId, item)
        .pipe(this.estimateRepo.ops.upsertOne(item => ({ item })))
        .pipe(
          handleMessage({
            type: requestMessage('qty_updated_estimate_item'),
          }),
        ),
    );
    // .pipe(
    //   tap(newItems => {
    //     if (item.isCustom) {
    //       const estimate = this.estimateRepo.getItem(estimateId) as TMEstimateResponse;
    //       if (!estimate) return;
    //       const group = estimate.groups.find(g => g.id === groupId);
    //       if (!group) return;
    //       this.estimateRepo.updateOne({
    //         id: estimateId,
    //         changes: () => ({
    //           groups: estimate.groups.map(g => (g.id === groupId ? { ...g, items: newItems } : g)),
    //         }),
    //       });
    //     } else {
    //       this.estimateRepo.upsertOne({ item: newItems });
    //     }
    //   }),
    // )
  };

  priceUpdate = (groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) => {
    return this.enqueue(() =>
      EstimateV2API.priceUpdate(groupId, item)
        .pipe(this.estimateRepo.ops.upsertOne(item => ({ item })))
        .pipe(
          handleMessage({
            type: requestMessage('price_updated_estimate_item'),
          }),
        ),
    );
  };
}
